/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 기능패널 : 버튼정의
//    - 공통-Common 	: 조회(btnQuery), 탭닫기(btnCloseTab), 저장(btnSave)  ← 화면 표시 순서
//    - 싱글-Single 	: 신규(btnNew), 삭제(btnDelete), 복사(btnCopy)
//    - 멀티-Grid 	: 행추가(btnInsertRow), 행삭제(btnDeleteRow), 행복사(btnCopyRow), 행취소(btnCancelRow)
//    - 도구-Tool 	: 엑셀로전환(btnExportExcel), 초기화(btnClear), 도움말(btnHelpDoc), 미리보기(btnPreview), 추가검색(btnConditionToggle)
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// src/components/common/NaviMenuBar.tsx
import React from "react";
import { Button, Stack, Paper } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 모듈
///   - 언어별 모듈 : useCommonTranslation
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useCommonTranslation } from '../translations';
import { useMsgBoxModal } from './CommonModal';
import { confirmQueryDespiteUnsavedGrid } from './AgGrid';
import { isLicenseReadOnly } from '../utils/licenseUtil'; // 라이선스 만료 후 읽기 전용(저장/삭제/신규/행편집 버튼 비활성)
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { PopupPgmContext, PopupCloseContext } from './ModalContext';   // 팝업 안에서 그려질 때의 팝업 프로그램 정보(DD생성용)
import { getUserInfo } from '../utils/userUtils';
import { getMsgText, getMsgType } from '../utils/commonUtils';

////////////////////////////////////////////////////////////////////////////////////////////////////////////
//  인터페이스(interface)
//    - NaviMenuButtonProps : 내비게이션 메뉴 버튼 속성 정의
//      . handler : 버튼 클릭 시 호출되는 함수
//      . show : 버튼 표시 여부
//      . disable : 버튼 비활성화 여부
//    - NaviMenuBarProps : 내비게이션 메뉴 바 속성 정의
//      . 공통(Common) 	: 조회(btnQuery), 저장(btnSave), 탭닫기(btnCloseTab)
//      . 싱글(Single) 	: 신규(btnNew), 삭제(btnDelete), 복사(btnCopy)
//      . 멀티(Grid) 	: 행추가(btnInsertRow), 행삭제(btnDeleteRow), 행복사(btnCopyRow), 행취소(btnCancelRow)
//      . 도구(Tool) 	: 엑셀로전환(btnExportExcel), 초기화(btnClear), 도움말(btnHelpDoc), 미리보기(btnPreview), 추가검색(btnConditionToggle)
////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface NaviMenuButtonProps {
  handler?: () => void;
  show?: boolean;
  disable?: boolean;
  icon?: string; // 기본 아이콘을 화면별로 바꿔야 할 때만 지정(예: 메일보내기 화면의 저장버튼 -> fa-paper-plane)
  label?: string; // 기본 라벨("저장" 등)을 화면별로 바꿔야 할 때만 지정
}

interface NaviMenuBarProps {
  // 공통
  naviMenuCommon?: boolean;
  btnQuery?: NaviMenuButtonProps;
  btnSave?: NaviMenuButtonProps;
  btnCloseTab?: NaviMenuButtonProps; // 이 화면(탭) 자체를 닫음(window.closeTab 호출) - 팝업(모달)을 닫는 btnClose와는 별개

  // 단일 레코드
  naviMenuSingle?: boolean;
  btnNew?: NaviMenuButtonProps;
  btnDelete?: NaviMenuButtonProps;
  btnCopy?: NaviMenuButtonProps;

  // 그리드
  naviMenuGrid?: boolean;
  btnInsertRow?: NaviMenuButtonProps;
  btnDeleteRow?: NaviMenuButtonProps;
  btnCopyRow?: NaviMenuButtonProps;
  btnCancelRow?: NaviMenuButtonProps;

  // 팝업
  naviMenuPopup?: boolean;
  btnConfirm?: NaviMenuButtonProps;
  btnClose?: NaviMenuButtonProps; //팝업닫기

  // 도구
  naviMenuTools?: boolean;
  btnClear?: NaviMenuButtonProps;
  btnExportExcel?: NaviMenuButtonProps;
  btnPreview?: NaviMenuButtonProps;
  btnHelpDoc?: NaviMenuButtonProps;
  btnConditionToggle?: NaviMenuButtonProps;
  btnGridArrayToggle?: NaviMenuButtonProps;
  // btnGenDD?: NaviMenuButtonProps;
  // 기타
  rowCount?: number;
  loading?: boolean;
  extraButtons?: React.ReactNode;  // 화면별 커스텀(더미 등) 버튼을 NaviMenuBar 수정 없이 추가할 때 사용
  // 조회 버튼 클릭 시 이 화면(callPgmId)의 그리드에 작업중(cud 있음)인 내용이 있으면 자동으로 확인창을 띄움 -
  // 넘기지 않으면(팝업 등 그리드가 없는 화면) 이 체크 자체를 생략.
  callPgmId?: string;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////
//  메인컴포넌트 : NaviMenuBar
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const NaviMenuBar: React.FC<NaviMenuBarProps> = ({
  // 공통
  naviMenuCommon = false,
  btnQuery = {},
  btnSave = {},
  btnCloseTab = {},
  // 단일 레코드
  naviMenuSingle = false,
  btnNew = {},
  btnDelete = {},
  btnCopy = {},
  // 그리드
  naviMenuGrid = false,
  btnInsertRow = {},
  btnDeleteRow = {},
  btnCopyRow = {},
  btnCancelRow = {},
  // 팝업
  naviMenuPopup = false,
  btnConfirm = {},
  btnClose = {},
  // 도구
  naviMenuTools = false,
  btnClear = {},
  btnExportExcel = {},
  btnPreview = {},
  btnHelpDoc = {},
  btnConditionToggle = {},
  btnGridArrayToggle = {},
  // btnGenDD = {},
  // 기타
  rowCount,
  loading = false,
  extraButtons,
  callPgmId,
}) => {
  // 공통라벨 번역
  const { ddKey } = useCommonTranslation();
  const { confirmMsgBox, showMsgBox } = useMsgBoxModal();

  // 팝업 [DD생성](2026-09-28) - 팝업 안의 메뉴바이고 DD생성 권한(사용자환경설정 DD생성, ZA_USER_CONFIG.DD_CRT_YN)이 있으면
  // 화면별 버튼 영역(extraButtons) 뒤에 자동으로 붙음. 팝업 소스(.tsx)의 라벨을 팝업 프로그램ID 기준으로 ZA_DD에 생성.
  //  (탭 화면의 DD생성은 메가메뉴의 [DD 생성]이 담당 - 팝업은 탭이 아니라서 그 버튼으로는 만들 수 없었음)
  const popupPgm = React.useContext(PopupPgmContext);
  // 팝업 안에서 [닫기](btnClose)를 그리면 팝업 틀에 알려 제목줄 [닫기]를 숨김(403/404 화면은 메뉴바가 없어 제목줄 [닫기]가 보임)
  const reportInnerClose = React.useContext(PopupCloseContext);
  const showsPopupClose = naviMenuPopup && !!btnClose.show;
  React.useEffect(() => {
    if (!reportInnerClose || !showsPopupClose) return;
    reportInnerClose(true);
    return () => reportInnerClose(false);
  }, [reportInnerClose, showsPopupClose]);
  const canPopupGenDD = !!popupPgm?.pgmId && getUserInfo()?.ddCrtYn === 'Y';
  const handlePopupGenDD = async () => {
    if (!popupPgm) return;
    const ok = await confirmMsgBox({ title: ddKey('lblConfirm') || '확인', msgText: `[${popupPgm.pgmId}] : ${ddKey('msgConfirmGenDD') || 'DD생성 하시겠습니까?'}`, showBtn: 'YesNo' });
    if (!ok) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiGenDD`, { url: popupPgm.callPgmId, menuId: popupPgm.pgmId });
      showMsgBox({ title: ddKey('lblConfirm') || '확인', msgText: `[${res.data?.menuId || popupPgm.pgmId}] 팝업에서 ${res.data?.count ?? 0}개의 라벨을 DD에 생성했습니다.`, showBtn: 'OK', msgType: 'info' });
    } catch (error: any) {
      showMsgBox({ title: ddKey('lblError') || '오류', msgText: getMsgText(error, 'DD생성 중 오류가 발생했습니다.'), showBtn: 'OK', msgType: getMsgType(error) });
    }
  };
  const popupGenDDButton = canPopupGenDD ? (
    <Tooltip title={ddKey('btnGenDD') || 'DD생성'}>
      <span className="tooltip-wrapper">
        <Button className='btnGenDD nm-btn' variant="outlined" size="small" onClick={handlePopupGenDD} disabled={loading}>
          <i className="fas fa-database w-4"></i>
          <span className='nm-label'>{ddKey('btnGenDD') || 'DD생성'}</span>
        </Button>
      </span>
    </Tooltip>
  ) : null;

  // 라이선스 만료 후 읽기 전용(2026-09-27) - 저장/삭제/신규/복사/행편집 버튼을 모든 화면에서 비활성으로 보여줌.
  // 조회/닫기/도구(엑셀·초기화 등)와 팝업 버튼은 그대로. 실제 차단은 서버(licenseRequestGuard)가 함.
  const licenseReadOnly = isLicenseReadOnly();
  if (licenseReadOnly) {
    const off = (b: NaviMenuButtonProps) => ({ ...b, disable: true });
    btnSave = off(btnSave); btnNew = off(btnNew); btnDelete = off(btnDelete); btnCopy = off(btnCopy);
    btnInsertRow = off(btnInsertRow); btnDeleteRow = off(btnDeleteRow); btnCopyRow = off(btnCopyRow); btnCancelRow = off(btnCancelRow);
  }

  // 조회 버튼 클릭 - 이 화면의 그리드에 작업중인 내용이 있으면 먼저 확인받은 뒤에만 화면의 실제 조회 핸들러 실행
  const handleQueryClick = async () => {
    if (callPgmId && !(await confirmQueryDespiteUnsavedGrid(callPgmId, confirmMsgBox, ddKey))) return;
    btnQuery.handler?.();
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  화면정의
  //		- 공통-common : 조회(btnQuery), 저장(btnSave)
  //    - 싱글-single : 신규(btnNew), 삭제(btnDelete), 복사(btnCopy)
  //    - 멀티-grid : 행추가(btnInsertRow), 행삭제(btnDeleteRow), 복사(btnCopyRow), 행취소(btnCancelRow)
  //    - 도구-tool : 엑셀로전환(btnExportExcel), 초기화(btnClear), 도움말(btnHelpDoc), 미리보기(btnPreview),
  //                  추가검색(btnConditionToggle), 그리드배열(btnGridArrayToggle)
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <Stack className="naviMenuBar" direction="row" alignItems="center">
      {/* [공통-Common] *************************************** */}
      {naviMenuCommon && (
        <Stack className="naviMenuCommon" direction="row" spacing={0.1} alignItems="center">
          {btnQuery.show && (
            <Tooltip title={btnQuery.label || ddKey('btnQuery') || '조회'}>
              <span className="tooltip-wrapper">
                <Button
                  className={`btnQuery nm-btn${btnQuery.label ? ' nm-btn-custom' : ''}`}
                  variant="outlined"
                  size="small"
                  onClick={handleQueryClick}
                  disabled={btnQuery.disable}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <>
                      <i className={`fas ${btnQuery.icon || 'fa-search'} w-4`}></i>
                      <span className='nm-label'>
                        {btnQuery.label || ddKey('btnQuery') || '조회'}
                      </span>
                    </>
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
          {/* 조회 - 닫기 - 저장 순서(2026-09-21). 팝업(btnClose/btnConfirm)은 별도 그룹이라 영향 없음 */}
          {btnCloseTab.show && (
            <Tooltip title={btnCloseTab.label || ddKey('btnCloseTab') || '닫기'}>
              <span className="tooltip-wrapper">
                <Button className={`btnCloseTab nm-btn${btnCloseTab.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnCloseTab.handler}
                  disabled={btnCloseTab.disable}
                >
                  <i className={`fas ${btnCloseTab.icon || 'fa-xmark'} w-4`}></i>
                    <span className='nm-label'>
                      {btnCloseTab.label || ddKey('btnCloseTab') || '닫기'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnSave.show && (
            <Tooltip title={btnSave.label || ddKey('btnSave') || '저장'}>
              <span className="tooltip-wrapper">
                <Button className={`btnSave nm-btn${btnSave.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnSave.handler}
                  disabled={btnSave.disable}
                >
                  <i className={`fas ${btnSave.icon || 'fa-save'} w-4`}></i>
                    <span className='nm-label'>
                      {btnSave.label || ddKey('btnSave') || '저장'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {/* [싱글-Single] *************************************** */}
      {naviMenuSingle && (
        <Stack className="naviMenuSingle" direction="row" spacing={0.1} alignItems="center">
          {btnNew.show && (
            <Tooltip title={btnNew.label || ddKey('btnNew') || '신규'}>
              <span className="tooltip-wrapper">
                <Button
                  className={`btnNew nm-btn${btnNew.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnNew.handler}
                  disabled={btnNew.disable}
                >
                  <i className={`fas ${btnNew.icon || 'fa-file'} w-4`}></i>
                    <span className='nm-label'>
                      {btnNew.label || ddKey('btnNew') || '신규'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnDelete.show && (
            <Tooltip title={btnDelete.label || ddKey('btnDelete') || '삭제'}>
              <span className="tooltip-wrapper">
                <Button className={`btnDelete nm-btn${btnDelete.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnDelete.handler}
                  disabled={btnDelete.disable}
                >
                  <i className={`fas ${btnDelete.icon || 'fa-trash'} w-4`}></i>
                    <span className='nm-label'>
                      {btnDelete.label || ddKey('btnDelete') || '삭제'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnCopy.show && (
            <Tooltip title={btnCopy.label || ddKey('btnCopy') || '복사'}>
              <span className="tooltip-wrapper">
                <Button className={`btnCopy nm-btn${btnCopy.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnCopy.handler}
                  disabled={btnCopy.disable}
                >
                  <i className={`fas ${btnCopy.icon || 'fa-copy'} w-4`}></i>
                    <span className='nm-label'>
                      {btnCopy.label || ddKey('btnCopy') || '복사'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {/* [멀티-Grid] *************************************** */}
      {naviMenuGrid && (
        <Stack className="naviMenuGrid" direction="row" spacing={0.1} alignItems="center">
          {btnInsertRow.show && (
            <Tooltip title={btnInsertRow.label || ddKey('btnInsertRow') || '행추가'}>
              <span className="tooltip-wrapper">
                <Button className={`btnInsertRow nm-btn${btnInsertRow.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnInsertRow.handler}
                  disabled={btnInsertRow.disable}
                >
                  <i className={`fas ${btnInsertRow.icon || 'fa-plus'} w-4`}></i>
                    <span className='nm-label'>
                      {btnInsertRow.label || ddKey('btnInsertRow') || '행추가'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnDeleteRow.show && (
            <Tooltip title={btnDeleteRow.label || ddKey('btnDeleteRow') || '행삭제'}>
              <span className="tooltip-wrapper">
                <Button className={`btnDeleteRow nm-btn${btnDeleteRow.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnDeleteRow.handler}
                  disabled={btnDeleteRow.disable}
                >
                  <i className={`fas ${btnDeleteRow.icon || 'fa-trash'} w-4`}></i>
                    <span className='nm-label'>
                      {btnDeleteRow.label || ddKey('btnDeleteRow') || '행삭제'}
                    </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnCopyRow.show && (
            <Tooltip title={btnCopyRow.label || ddKey('btnCopyRow') || '행복사'}>
              <span className="tooltip-wrapper">
                <Button className={`btnCopyRow nm-btn${btnCopyRow.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnCopyRow.handler}
                  disabled={btnCopyRow.disable}
                >
                  <i className={`fas ${btnCopyRow.icon || 'fa-copy'} w-4`}></i>
                  <span className='nm-label'>
                    {btnCopyRow.label || ddKey('btnCopyRow') || '행복사'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnCancelRow.show && (
            <Tooltip title={btnCancelRow.label || ddKey('btnCancelRow') || '행취소'}>
              <span className="tooltip-wrapper">
                <Button className={`btnCancelRow nm-btn${btnCancelRow.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnCancelRow.handler}
                  disabled={btnCancelRow.disable}
                >
                  <i className={`fas ${btnCancelRow.icon || 'fa-ban'} w-4`}></i>
                  <span className='nm-label'>
                    {btnCancelRow.label || ddKey('btnCancelRow') || '행취소'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {/* [팝업] */}
      {naviMenuPopup && (
        <Stack className="naviMenuPopup" direction="row" alignItems="center">
          {btnConfirm.show && (
            <Tooltip title={btnConfirm.label || ddKey('btnConfirm') || '확인'}>
              <span className="tooltip-wrapper">
                <Button className={`btnClear nm-btn${btnConfirm.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnConfirm.handler}
                  disabled={btnConfirm.disable}
                  >
                  <i className={`fas ${btnConfirm.icon || 'fa-check'}`}></i>
                  <span className='nm-label'>
                    {btnConfirm.label || ddKey('btnConfirm') || '확인'}
                  </span>
                  </Button>
              </span>
            </Tooltip>
          )}
          {btnClose.show && (
            <Tooltip title={btnClose.label || ddKey('btnClose') || '닫기'}>
              <span className="tooltip-wrapper">
                <Button className={`btnHelpDoc nm-btn${btnClose.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnClose.handler}
                  disabled={btnClose.disable}
                  >
                  <i className={`fas ${btnClose.icon || 'fa-xmark'}`}></i>
                  <span className='nm-label'>
                    {btnClose.label || ddKey('btnClose') || '닫기'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {/* [도구] */}
      {naviMenuTools && (
        <Stack className="naviMenuTools" direction="row" spacing={0.1} alignItems="center">
          {btnClear.show && (
            <Tooltip title={btnClear.label || ddKey('btnClear') || '초기화'}>
              <span className="tooltip-wrapper">
                <Button className={`btnClear nm-btn${btnClear.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnClear.handler}
                  disabled={btnClear.disable}
                >
                  <i className={`fas ${btnClear.icon || 'fa-file'} w-4`}></i>
                  <span className='nm-label'>
                    {btnClear.label || ddKey('btnClear') || '초기화'}
                  </span>
                  </Button>
              </span>
            </Tooltip>
          )}
          {btnHelpDoc.show && (
            <Tooltip title={btnHelpDoc.label || ddKey('btnHelpDoc') || '도움말'}>
              <span className="tooltip-wrapper">
                <Button className={`btnHelpDoc nm-btn${btnHelpDoc.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnHelpDoc.handler}
                  disabled={btnHelpDoc.disable}
                >
                  <i className={`fas ${btnHelpDoc.icon || 'fa-info'} w-4`}></i>
                  <span className='nm-label'>
                    {btnHelpDoc.label || ddKey('btnHelpDoc') || '도움말'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnExportExcel.show && (
            <Tooltip title={btnExportExcel.label || ddKey('btnExportExcel') || '엑셀저장'}>
              <span className="tooltip-wrapper">
                <Button className={`btnExportExcel nm-btn${btnExportExcel.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnExportExcel.handler}
                  disabled={btnExportExcel.disable}
                >
                  <i className={`fas ${btnExportExcel.icon || 'fa-file-excel'} w-4`}></i>
                  <span className='nm-label'>
                    {btnExportExcel.label || ddKey('btnExportExcel') || '엑셀저장'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnPreview.show && (
            <Tooltip title={btnPreview.label || ddKey('btnPreview') || '미리보기'}>
              <span className="tooltip-wrapper">
                <Button className={`btnPreview nm-btn${btnPreview.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnPreview.handler}
                  disabled={btnPreview.disable}
                >
                  <i className={`fas ${btnPreview.icon || 'fa-file-excel'} w-4`}></i>
                  <span className='nm-label w-60'>
                    {btnPreview.label || ddKey('btnPreview') || '미리보기'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnConditionToggle.show && (
            <Tooltip title={btnConditionToggle.label || ddKey('btnConditionToggle') || '추가검색'}>
              <span className="tooltip-wrapper">
                <Button className={`btnConditionToggleShow nm-btn${btnConditionToggle.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnConditionToggle.handler}
                  disabled={btnConditionToggle.disable}
                >
                  <i className={`fas ${btnConditionToggle.icon || 'fa-info'} w-4`}></i>
                  <span id="txtConditionToggle" className='nm-label'>
                    {btnConditionToggle.label || ddKey('btnConditionToggleShow') || '검색보임'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {btnGridArrayToggle.show && (
            <Tooltip title={btnGridArrayToggle.label || ddKey('btnGridArrayToggle') || '그리드배열'}>
              <span className="tooltip-wrapper">
                <Button className={`btnGridArrayToggle nm-btn${btnGridArrayToggle.label ? ' nm-btn-custom' : ''}`} variant="outlined" size="small"
                  onClick={btnGridArrayToggle.handler}
                  disabled={btnGridArrayToggle.disable}
                >
                  <i className={`fas ${btnGridArrayToggle.icon || 'fa-th-large'} w-4`}></i>
                  <span className='nm-label'>
                    {btnGridArrayToggle.label || ddKey('btnGridArrayToggle') || '배열'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )}
          {/* {btnGenDD.show && (
            <Tooltip title={ddKey('btnGenDD') || 'DD생성'}>
              <span className="tooltip-wrapper">
                <Button className='btnGenDD nm-btn' variant="outlined" size="small"
                  onClick={btnGenDD.handler}
                  disabled={btnGenDD.disable}
                >
                  <i className="fas fa-database w-4"></i>
                  <span className='nm-label'>
                    {ddKey('btnGenDD') || 'DD생성'}
                  </span>
                </Button>
              </span>
            </Tooltip>
          )} */}
          {/* [행 개수 표시] */}
          {rowCount !== undefined && <span>{ddKey('msgRowCount', { rowCount }) || `행 수: ${rowCount}`}</span>}
        </Stack>
      )}

      {/* [화면별 커스텀 버튼] : NaviMenuBar 수정 없이 화면에서 자유롭게 버튼 추가 */}
      {(extraButtons || popupGenDDButton) && (
        <Stack className="naviMenuExtra" direction="row" spacing={0.1} alignItems="center">
          {extraButtons}
          {popupGenDDButton}
        </Stack>
      )}

      {/* [라이선스 읽기 전용 표시] : 만료 후 읽기 전용이면 저장 계열 버튼이 왜 꺼졌는지 알려줌 */}
      {licenseReadOnly && (
        <Tooltip title={ddKey('msgLicenseReadOnly') || '라이선스가 만료되어 읽기 전용입니다. 조회만 할 수 있습니다.'}>
          <span className="naviMenuReadOnly" style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', whiteSpace: 'nowrap' }}>
            <i className="fas fa-lock" style={{ marginRight: 4 }} />{ddKey('lblLicenseReadOnly') || '읽기 전용'}
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

export default NaviMenuBar;

/////////////////////////////////////////////////////////////////////////////////////////////////////////
